import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useJournalEntriesStore } from '@/views/apps/journals/store/journalEntriesStore'
import { useJournalDetailStore } from '@/views/apps/journals/store/journalDetailStore'
import type {
  ProcessedEntry,
  TableHeader,
} from '@/views/apps/journals/types/table.types'
import type { TableOptions } from '@/modules/journals/composables/useServerTableOptions'
import { useServerTableOptions } from '@/modules/journals/composables/useServerTableOptions'

interface UseJournalDetailTableParams {
  journalId: { value: number }
}

interface UpdateEntriesPaginationOptions {
  page?: number
  itemsPerPage?: number
}

export function useJournalDetailTable({
  journalId,
}: UseJournalDetailTableParams) {
  const detailStore = useJournalDetailStore()
  const entriesStore = useJournalEntriesStore()

  const { journalAttributes } = storeToRefs(detailStore)

  const { journalEntries, itemsPerPage, entriesResponseData }
    = storeToRefs(entriesStore)

  const {
    page,
    lastFetchedPage,
    lastFetchedItemsPerPage,
    currentSort,
    isSamePageRequest,
    markFetched,
    resetToFirstPage,
    toggleSort,
  } = useServerTableOptions({
    initialPage: 1,
    initialItemsPerPage: itemsPerPage.value,
    initialSort: null,
  })

  const visibleAttributes = computed(() =>
    journalAttributes.value.filter(attr => attr.is_visible !== false),
  )

  const getColumnWidth = (attr: { fixed_width: number | null; table_width?: number }) => {
    if (attr.fixed_width != null) {
      return `${attr.fixed_width}px`
    }

    if (attr.table_width == null) {
      return '200px'
    }

    const tw = Number(attr.table_width)
    if (!Number.isFinite(tw) || tw <= 0) {
      return '200px'
    }

    // API contract: table_width=12 corresponds to 1920px.
    const widthPx = Math.round((tw / 12) * 1920)

    return `${widthPx}px`
  }

  const tableHeaders = computed<TableHeader[]>(() => {
    // Сортировка управляется кастомными кнопками (handleSort), поэтому встроенная
    // сортировка таблицы выключена — таблица не эмитит конкурирующий sortBy.
    const numberColumn: TableHeader = {
      title: '№',
      key: 'number',
      sortable: false,
      width: '100px',
      minWidth: '100px',
      align: 'center',
    }

    const attributeHeaders = visibleAttributes.value.map(attr => {
      const displayName = attr.short_name || attr.attribute_name
      const title = attr.unit ? `${displayName} (${attr.unit})` : displayName

      const colWidth = getColumnWidth(attr)

      return {
        title,
        key: `attribute_${attr.id}`,
        sortable: false,
        width: colWidth,
        minWidth: colWidth,
      }
    })

    return [numberColumn, ...attributeHeaders]
  })

  const processedEntries = computed<ProcessedEntry[]>(() => {
    return journalEntries.value.map(entry => {
      const processedEntry: ProcessedEntry = {
        id: entry.id,
        number: entry.number ?? null,
        original: entry,
      }

      const processedEntryRecord = processedEntry as unknown as Record<
        string,
        unknown
      >

      const valuesByTemplateAttrId = new Map<number, unknown>()
      if (entry.attribute_values && Array.isArray(entry.attribute_values)) {
        entry.attribute_values.forEach(attrValue => {
          const templateAttrId = (
            attrValue as unknown as { template_attribute_id?: number }
          ).template_attribute_id

          if (templateAttrId != null && attrValue.value !== undefined) {
            valuesByTemplateAttrId.set(templateAttrId, attrValue.value)
          }
        })
      }

      visibleAttributes.value.forEach(attr => {
        const key = `attribute_${attr.id}`
        const value = valuesByTemplateAttrId.get(attr.id)
        const mappedValue = value !== undefined ? value : ''

        processedEntryRecord[key] = mappedValue
      })

      return processedEntry
    })
  })

  const totalEntriesCount = computed(() => {
    const count = entriesResponseData.value?.count
    if (count != null && count >= 0) {
      return count
    }

    return journalEntries.value.length
  })

  function extractCursorFromUrl(url: string | null): string | undefined {
    if (!url) {
      return undefined
    }

    try {
      const fullUrl = url.startsWith('http')
        ? url
        : `https://dummy${url.startsWith('?') ? '' : '/'}${url}`

      const u = new URL(fullUrl)

      return u.searchParams.get('cursor') ?? undefined
    } catch {
      const m = url.match(/[?&]cursor=([^&]+)/)

      return m ? m[1] : undefined
    }
  }

  const mapSortFieldToApi = (columnKey: string, order: 'asc' | 'desc') => {
    let sortString = columnKey

    if (columnKey.startsWith('attribute_')) {
      const attributeId = columnKey.replace('attribute_', '')

      const attr = journalAttributes.value.find(
        a => a.id.toString() === attributeId,
      )

      if (attr) {
        sortString = `template_attribute__${attr.id}`
      }
    }

    return order === 'desc' ? `-${sortString}` : sortString
  }

  const getSortIcon = (columnKey: string) => {
    if (!currentSort.value || currentSort.value.key !== columnKey) {
      return 'ri-arrow-up-down-line'
    }

    return currentSort.value.order === 'asc'
      ? 'ri-arrow-up-line'
      : 'ri-arrow-down-line'
  }

  const handleSort = async (columnKey: string) => {
    const nextSort = toggleSort(columnKey)

    entriesStore.setCursor(undefined)
    resetToFirstPage()
    entriesStore.setEntrySort(mapSortFieldToApi(nextSort.key, nextSort.order))

    if (journalId.value) {
      await entriesStore.fetchJournalEntries(journalId.value)
    }

    // Фиксируем состояние, чтобы повторный update:options после ремаунта таблицы
    // (из-за смены :key по entrySort) не вызвал лишний запрос пагинации.
    markFetched(1, entriesStore.itemsPerPage)
  }

  async function updateEntriesPagination(opts: UpdateEntriesPaginationOptions) {
    if (!journalId.value) {
      return
    }

    const newPage = opts.page ?? page.value
    const newItemsPerPage = opts.itemsPerPage ?? entriesStore.itemsPerPage

    if (isSamePageRequest(newPage, newItemsPerPage)) {
      return
    }

    if (newItemsPerPage !== lastFetchedItemsPerPage.value) {
      entriesStore.itemsPerPage = newItemsPerPage
      entriesStore.setCursor(undefined)
      resetToFirstPage()
      await entriesStore.fetchJournalEntries(journalId.value)
      markFetched(1, newItemsPerPage)

      return
    }

    if (newPage < lastFetchedPage.value) {
      const prev = entriesResponseData.value?.previous
      if (!prev) {
        return
      }

      entriesStore.setCursor(extractCursorFromUrl(prev))
      await entriesStore.fetchJournalEntries(journalId.value)
      page.value = Math.max(1, newPage)
      markFetched(page.value, newItemsPerPage)

      return
    }

    if (newPage > lastFetchedPage.value) {
      const next = entriesResponseData.value?.next
      if (!next) {
        return
      }

      entriesStore.setCursor(extractCursorFromUrl(next))
      await entriesStore.fetchJournalEntries(journalId.value)
      page.value = newPage
      markFetched(page.value, newItemsPerPage)
    }
  }

  async function onEntriesOptionsUpdate(options: TableOptions) {
    await updateEntriesPagination({
      page: options.page,
      itemsPerPage: options.itemsPerPage,
    })
  }

  const syncAfterInitialFetch = () => {
    markFetched(page.value, entriesStore.itemsPerPage)
  }

  return {
    getSortIcon,
    handleSort,
    itemsPerPage,
    onEntriesOptionsUpdate,
    page,
    processedEntries,
    syncAfterInitialFetch,
    tableHeaders,
    totalEntriesCount,
    visibleAttributes,
  }
}
